'use client';

import { Search, XIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import TaxonomyCombobox from '@/components/forms/TaxonomyCombobox';
import { useTaxonomyCategories } from '@/lib/taxonomy-service';

export interface InstituteFiltersProps {
  countryId: string;
  onCountryChange: (value: string) => void;
  stateId: string;
  onStateChange: (value: string) => void;
  cityId: string;
  onCityChange: (value: string) => void;
  search: string;
  onSearchChange: (value: string) => void;
  className?: string;
}

export function InstituteFilters({
  countryId,
  onCountryChange,
  stateId,
  onStateChange,
  cityId,
  onCityChange,
  search,
  onSearchChange,
  className,
}: InstituteFiltersProps) {
  const { countries, states } = useTaxonomyCategories('countries', 'states');

  const selectedCountry = countries.find((item) => String(item.id) === countryId);
  const filteredStates = selectedCountry
    ? states.filter((state) => state.country === selectedCountry.id)
    : [];

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <input
            type="text"
            placeholder="Search institute name..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full min-h-11 rounded-lg border border-border bg-background py-2.5 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex min-w-0 flex-1 gap-2">
          <TaxonomyCombobox
            category="countries"
            valueField="id"
            placeholder="All countries"
            value={countryId || null}
            onChange={(nextValue) => onCountryChange(nextValue ?? '')}
            wrapperClassName="min-w-0 flex-1"
          />
          {countryId && (
            <button
              type="button"
              aria-label="Clear country filter"
              onClick={() => {
                onCountryChange('');
                onStateChange('');
                onCityChange('');
              }}
              className="min-h-11 rounded-lg border border-neon-cyan/10 bg-card px-3 text-ash transition hover:bg-panel hover:text-neon-cyan focus-visible:ring-2 focus-visible:ring-neon-cyan/50"
            >
              <XIcon aria-hidden="true" data-icon="inline-start" />
            </button>
          )}
        </div>

        {filteredStates.length > 0 && (
          <div className="flex min-w-0 flex-1 gap-2">
            <TaxonomyCombobox
              category="states"
              valueField="id"
              placeholder="All states"
              value={stateId || null}
              onChange={(nextValue) => {
                onStateChange(nextValue ?? '');
                onCityChange('');
              }}
              filter={(state) => state.country === Number(countryId)}
              disabled={!countryId}
              wrapperClassName="min-w-0 flex-1"
            />
            {stateId && (
              <button
                type="button"
                aria-label="Clear state filter"
                onClick={() => {
                  onStateChange('');
                  onCityChange('');
                }}
                className="min-h-11 rounded-lg border border-neon-cyan/10 bg-card px-3 text-ash transition hover:bg-panel hover:text-neon-cyan focus-visible:ring-2 focus-visible:ring-neon-cyan/50"
              >
                <XIcon aria-hidden="true" data-icon="inline-start" />
              </button>
            )}
          </div>
        )}

        <div className="flex min-w-0 flex-1 gap-2">
          <TaxonomyCombobox
            category="cities"
            valueField="id"
            placeholder="All cities"
            value={cityId || null}
            onChange={(nextValue) => onCityChange(nextValue ?? '')}
            filter={(city) =>
              city.country === Number(countryId) &&
              (!stateId || city.state === Number(stateId))
            }
            disabled={!countryId}
            wrapperClassName="min-w-0 flex-1"
          />
          {cityId && (
            <button
              type="button"
              aria-label="Clear city filter"
              onClick={() => onCityChange('')}
              className="min-h-11 rounded-lg border border-neon-cyan/10 bg-card px-3 text-ash transition hover:bg-panel hover:text-neon-cyan focus-visible:ring-2 focus-visible:ring-neon-cyan/50"
            >
              <XIcon aria-hidden="true" data-icon="inline-start" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
